import React, { useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Tabs, Tab } from '@mui/material';

const HelpDialog = ({ open, onClose }) => {
    const [tabIndex, setTabIndex] = useState(0); // Track which tab is selected

    const handleTabChange = (event, newIndex) => {
        setTabIndex(newIndex);
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
            <DialogTitle>Help</DialogTitle>
            <DialogContent>
                <Tabs value={tabIndex} onChange={handleTabChange} centered>
                    <Tab label="General Guide" />
                    <Tab label="Frequently Asked Questions" />
                </Tabs>

                {tabIndex === 0 && (
                    <>
                        <h2>General Guide</h2>

                        <h3>Reservations Page</h3>
                        <p>
                            The Reservations page includes two tables: one for reservations scheduled for checkout and another for check-in.
                        </p>

                        <h5>General Functionality:</h5>
                        <ul>
                            <li>You can choose which reservations to display using the date picker located to the right of the title.</li>
                            <li>You can search for a reservation using any parameter, such as ID, name, or product.</li>
                            <li>You can cancel your reservation before checkout.</li>
                        </ul>

                        <h5>Staff Functionality:</h5>
                        <ul>
                            <li>The "Add Reservation" button allows staff to quickly create a reservation.</li>
                            <li>You can update the check-in date for items after they have been checked out.</li>
                            <li>To check in or check out items, click on the item row and press the corresponding button.</li>
                        </ul>

                        <h3>Inventory Page</h3>
                        <p>
                            The Inventory page displays all items that are currently available or unavailable.
                        </p>

                        <h5>General Functionality:</h5>
                        <ul>
                            <li>You can search for items using any parameter, such as name, type, or availability.</li>
                            <li>You can schedule an item for checkout by clicking on the item row.</li>
                            <li>The table has built-in features for filtering and sorting items.</li>
                        </ul>

                        <h5>Staff Functionality:</h5>
                        <ul>
                            <li>The "Add" button allows you to add new items to the system.</li>
                            <li>The "Import CSV" button allows you to bulk import items from a CSV file.</li>
                            <li>You can edit an item's details using the pencil icon in the item's row.</li>
                            <li>You can delete an item using the trash icon in the item's row.</li>
                            <li>When adding or editing items, you can add new locations and student classes on the fly if they don't exist.</li>
                            <li>Items can include kit items, which are sub-items that are part of a larger kit.</li>
                        </ul>

                        <h5>Availability Filtering:</h5>
                        <ul>
                            <li>Use the "Filter by Date/Time Availability" button to check which items are available at a specific date and time.</li>
                            <li>This filter shows only items that can be reserved for the selected date and time slot.</li>
                            <li>Click "Reset" to clear the filter and view all items again.</li>
                        </ul>

                        <h3>Students Page</h3>
                        <p>
                            This page displays all students in the system, whether they have active or inactive accounts.
                        </p>

                        <h5>Staff Functionality:</h5>
                        <ul>
                            <li>The "Add" button allows you to add new students to the system.</li>
                            <li>You can import multiple students at once using CSV import functionality.</li>
                            <li>You can edit a student's details using the pencil icon in the student's row.</li>
                            <li>You can delete a student using the trash icon in the student's row.</li>
                            <li>The plus (+) icon allows you to create an account for the student.</li>
                        </ul>

                        <h3>User Page</h3>
                        <p>
                            This page displays all users in the system, including those with active or inactive accounts.
                        </p>

                        <h5>Staff Functionality:</h5>
                        <ul>
                            <li>The "Add" button allows you to add new users to the system. Staff can only create student accounts, while admins can create both staff and admin accounts.</li>
                            <li>You can edit a user's details using the pencil icon in the user's row.</li>
                        </ul>

                        <h3>Profile Management</h3>
                        <p>
                            Access your profile settings through the profile menu in the top right corner.
                        </p>

                        <h5>Profile Features:</h5>
                        <ul>
                            <li>You can edit your username and password from the profile menu.</li>
                            <li>To change your password, you must provide your current password for security.</li>
                        </ul>

                        <h3>Login & Password Recovery</h3>
                        <p>
                            The login page provides options for account access and password recovery.
                        </p>

                        <h5>Password Recovery:</h5>
                        <ul>
                            <li>If you forget your password, click "Forgot Password" on the login page.</li>
                            <li>Enter your username to receive a verification code via email.</li>
                            <li>Enter the verification code to proceed with resetting your password.</li>
                            <li>Create a new password and confirm it to complete the reset process.</li>
                        </ul>
                    </>

                )}

                {tabIndex === 1 && (
                    <>
                    <h2>Frequently Asked Questions</h2>
                    <h5>Reservations</h5>
                    <p><strong>Q: How can I filter reservations by date?</strong></p>
                    <p>A: Use the date picker located to the right of the title to choose which reservations to display.</p>
                
                    <p><strong>Q: Can I cancel a reservation?</strong></p>
                    <p>A: Yes, you can cancel your reservation before checkout.</p>
                
                    <p><strong>Q: How can staff check in or check out items?</strong></p>
                    <p>A: Staff can click on the item row in the table and press the corresponding button to check in or check out items.</p>
                
                    <h5>Inventory</h5>
                    <p><strong>Q: How can I search for a specific item?</strong></p>
                    <p>A: You can search for an item using any parameter, such as name, type, or availability, in the search bar.</p>
                
                    <p><strong>Q: How can staff add new items to the inventory?</strong></p>
                    <p>A: Staff can use the "Add" button to add new items to the system.</p>
                
                    <p><strong>Q: How do I schedule an item for checkout?</strong></p>
                    <p>A: Click on the item row in the Inventory table to view item details, then click "Create Reservation" to schedule it for checkout.</p>
                
                    <p><strong>Q: Can I import multiple items at once?</strong></p>
                    <p>A: Yes, staff can use the "Import CSV" button to bulk import items from a CSV file. The system will automatically create locations and student classes if they don't exist.</p>
                
                    <p><strong>Q: How can I check if an item is available at a specific time?</strong></p>
                    <p>A: Use the "Filter by Date/Time Availability" button on the Inventory page. Select a date and time to see only items available for that time slot.</p>
                
                    <p><strong>Q: What are kit items?</strong></p>
                    <p>A: Kit items are sub-items that are part of a larger kit. When adding or editing an inventory item, you can add multiple kit items with their own details (manufacturer, model, serial number, etc.).</p>
                
                    <p><strong>Q: Can I add new locations or student classes when adding inventory?</strong></p>
                    <p>A: Yes, when adding or editing an inventory item, you can select "+ Add New Location" or "+ Add New Class" from the dropdown menus to create them on the fly.</p>
                
                    <h5>Students</h5>
                    <p><strong>Q: What does the Students page display?</strong></p>
                    <p>A: The Students page displays all students in the system, including those with active or inactive accounts.</p>
                
                    <p><strong>Q: Can staff create accounts for students?</strong></p>
                    <p>A: Yes, staff can use the plus (+) icon to create accounts for students.</p>
                
                    <p><strong>Q: Can I import multiple students at once?</strong></p>
                    <p>A: Yes, staff can import students from a CSV file. The system will validate the data and report any errors or skipped entries.</p>
                
                    <h5>Users</h5>
                    <p><strong>Q: What does the User page display?</strong></p>
                    <p>A: The User page displays all users in the system, including those with active or inactive accounts.</p>
                
                    <p><strong>Q: Can staff create new user accounts?</strong></p>
                    <p>A: Yes, staff can create new student accounts, while admins can create both staff and admin accounts.</p>
                
                    <p><strong>Q: How can user details be edited?</strong></p>
                    <p>A: Use the pencil icon in the user's row to edit their details.</p>
                
                    <h5>Profile & Account</h5>
                    <p><strong>Q: How can I change my password?</strong></p>
                    <p>A: Click on your profile icon in the top right corner, select "Edit Profile", and enter your current password along with your new password.</p>
                
                    <p><strong>Q: Can I change my username?</strong></p>
                    <p>A: Yes, you can change your username from the Edit Profile dialog. You'll need to provide your current password to make changes.</p>
                
                    <h5>Login & Password Recovery</h5>
                    <p><strong>Q: What should I do if I forget my password?</strong></p>
                    <p>A: Click "Forgot Password" on the login page, enter your username, and follow the instructions to receive a verification code via email. Use the code to reset your password.</p>
                
                    <p><strong>Q: How long is the verification code valid?</strong></p>
                    <p>A: Verification codes are sent to your registered email address. There is a cooldown period between code requests to prevent abuse.</p>
                </>                
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Close</Button>
            </DialogActions>
        </Dialog>
    );
};

export default HelpDialog;
